/**
 * Export builder for a regular (non-consolidated) location in the Monthly Seasonality view.
 * Builds the per-department export rows with full expense category and account detail,
 * from the same aggregates and helpers the screen renders. Extracted verbatim from
 * SeasonalityModeView.handleExportExcel; the consolidated branch lives in
 * utils/consolidatedExportBuilder.ts.
 */

import { MonthlyTotals, createEmptyMonthlyTotals, addMonthlyTotals } from '../../types';
import { DepartmentTemplate, resolveAccountsForDepartment } from '../../config/docTemplate';
import { DepartmentExportData, ExportRow, ExportCategory, ExportAccount } from '../../utils/projectionsExport';
import { DepartmentAggregates } from './seasonalityModeTypes';
import { SeasonalityModeData } from './useSeasonalityModeData';

interface BuildLocationExportArgs {
  visibleDepartments: DepartmentTemplate[];
  data: SeasonalityModeData;
  departmentAggregates: DepartmentAggregates[];
  isConsolidated: boolean;
}

export function buildLocationExportDepartments({
  visibleDepartments,
  data,
  departmentAggregates,
  isConsolidated,
}: BuildLocationExportArgs): DepartmentExportData[] {
  const {
    extraDisplayAccountCodes,
    inputAccountCodes,
    calculationsMap,
    actualAccountMap,
    accountMap2023,
    accountMap2024,
    accountMap2025,
    hasAccountData,
    getDeptGrossSeasonalityWeights,
    getDeptExpenseSeasonalityWeights,
    getDeptUnitSeasonalityWeights,
    getStoredMonthlyDistribution,
    getVehicleDeptMonthlyGross,
    getCategoryMonthlyExpense,
  } = data;

  // Build department export data with full expense category and account-level detail
  // For regular locations, use departmentAggregates
  return visibleDepartments.map(deptOriginal => {
    // Resolve suffix-based pattern matching for export (discovers new Body Shop accounts)
    const allAccountCodes = Array.from(accountMap2023.keys()).concat(Array.from(accountMap2024.keys()), Array.from(accountMap2025.keys()), Array.from(actualAccountMap.keys()), Array.from(extraDisplayAccountCodes), Array.from(inputAccountCodes));
    const resolvedCats = resolveAccountsForDepartment(deptOriginal, allAccountCodes);
    const dept: DepartmentTemplate = resolvedCats !== deptOriginal.expenseCategories
      ? { ...deptOriginal, expenseCategories: resolvedCats }
      : deptOriginal;

    const displayName = dept.displayName;
    const rows: ExportRow[] = [];

    let projectedGross = 0;
    let monthlyGross = createEmptyMonthlyTotals();
    let projectedExpenses = 0;
    let monthlyExpenses = createEmptyMonthlyTotals();
    let projectedUnits = 0;
    let monthlyUnits: MonthlyTotals | undefined;

    // Get department-level seasonality weights for expense calculations
    const deptGrossWeights = getDeptGrossSeasonalityWeights(dept.name);
    const deptExpenseWeights = getDeptExpenseSeasonalityWeights(dept.name);
    const grossIsZero = deptGrossWeights.every(w => Math.abs(w - 1/12) < 0.0001);
    const deptWeights = grossIsZero ? deptExpenseWeights : deptGrossWeights;

    if (isConsolidated) {
      // For consolidated view, read from calculationsMap (matches renderDepartment logic)
      if (dept.hasFandI) {
        // Vehicle departments: use TOTAL_GROSS
        const storedTotalGross = calculationsMap.get(`${dept.name}#TOTAL_GROSS`);
        if (storedTotalGross) {
          monthlyGross = { ...storedTotalGross } as MonthlyTotals;
          projectedGross = Object.values(storedTotalGross).reduce((sum, val) => sum + (val || 0), 0);
        } else {
          // Fall back to GROSS + FI_INCOME
          const storedGross = calculationsMap.get(`${dept.name}#GROSS`);
          if (storedGross) {
            monthlyGross = { ...storedGross } as MonthlyTotals;
            projectedGross = Object.values(storedGross).reduce((sum, val) => sum + (val || 0), 0);
          }
          const storedFI = calculationsMap.get(`${dept.name}#FI_INCOME`) ||
                          calculationsMap.get(`${dept.name}#F_AND_I`);
          if (storedFI) {
            projectedGross += Object.values(storedFI).reduce((sum, val) => sum + (val || 0), 0);
            monthlyGross = addMonthlyTotals(monthlyGross, storedFI as MonthlyTotals);
          }
        }
        // Units
        const storedUnits = calculationsMap.get(`${dept.name}#UNITS`);
        if (storedUnits) {
          monthlyUnits = { ...storedUnits } as MonthlyTotals;
          projectedUnits = Object.values(storedUnits).reduce((sum, val) => sum + (val || 0), 0);
        }
      } else if (dept.name === 'RENTAL') {
        const storedRental = calculationsMap.get(`${dept.name}#RENTAL_INCOME`) ||
                            calculationsMap.get(`${dept.name}#GROSS`);
        if (storedRental) {
          monthlyGross = { ...storedRental } as MonthlyTotals;
          projectedGross = Object.values(storedRental).reduce((sum, val) => sum + (val || 0), 0);
        }
      } else {
        // SERVICE, PARTS, BODY_SHOP, OTHERS, MISC
        const storedGross = calculationsMap.get(`${dept.name}#GROSS`);
        if (storedGross) {
          monthlyGross = { ...storedGross } as MonthlyTotals;
          projectedGross = Object.values(storedGross).reduce((sum, val) => sum + (val || 0), 0);
        }
      }

      // Expenses for consolidated
      const storedExpenses = calculationsMap.get(`${dept.name}#TOTAL_EXPENSES`);
      if (storedExpenses) {
        monthlyExpenses = { ...storedExpenses } as MonthlyTotals;
        projectedExpenses = Object.values(storedExpenses).reduce((sum, val) => sum + (val || 0), 0);
      }
    } else {
      // For regular locations, use departmentAggregates for the totals and expenses; the gross
      // and units months are what the screen shows (the stored, possibly Executive-adjusted
      // spread when it matches the total), since the aggregates carry the seasonality base
      const agg = departmentAggregates.find(a => a.department === dept.name);
      if (agg) {
        projectedGross = agg.gross;
        monthlyGross = dept.hasFandI
          ? getVehicleDeptMonthlyGross(dept.name, agg.gross, deptGrossWeights)
          : getStoredMonthlyDistribution(dept.name, dept.name === 'RENTAL' ? 'RENTAL_INCOME' : 'GROSS', agg.gross, deptGrossWeights);
        projectedExpenses = agg.totalExpenses;
        monthlyExpenses = agg.totalExpensesMonthly;
        if (agg.units !== undefined) {
          projectedUnits = agg.units;
          monthlyUnits = agg.units > 0
            ? getStoredMonthlyDistribution(dept.name, 'UNITS', agg.units, getDeptUnitSeasonalityWeights(dept.name))
            : agg.unitsMonthly;
        }
      }
    }

    // Calculate profit
    const projectedProfit = projectedGross - projectedExpenses;
    const monthlyProfit = createEmptyMonthlyTotals();
    for (let m = 1; m <= 12; m++) {
      const key = `month${m}` as keyof MonthlyTotals;
      monthlyProfit[key] = monthlyGross[key] - monthlyExpenses[key];
    }

    // Add gross row
    if (dept.name !== 'MISC') {
      rows.push({
        label: dept.name === 'OTHERS' ? 'Other Income' : (dept.name === 'RENTAL' ? 'Rental Income' : 'Total Gross'),
        monthlyValues: monthlyGross,
        annualTotal: projectedGross,
        isTotalRow: true,
      });
    }

    // Add units for vehicle departments
    if (projectedUnits > 0 && monthlyUnits) {
      rows.push({
        label: 'Units',
        monthlyValues: monthlyUnits,
        annualTotal: projectedUnits,
        isUnits: true,
      });
    }

    // Build expense categories with account-level detail
    const exportCategories: ExportCategory[] = [];

    // Skip expense categories for OTHERS (income-only) and MISC
    if (dept.name !== 'OTHERS' && dept.name !== 'MISC') {
      // Filter to expense categories only (not income categories)
      const expenseCategoriesFiltered = dept.expenseCategories.filter(
        cat => !['RENTAL_INCOME', 'OTHER_INCOME'].includes(cat.name)
      );

      expenseCategoriesFiltered.forEach(category => {
        // Get accounts with historical data
        const accountsWithData = category.accounts.filter(acc => {
          return hasAccountData(acc.code);
        });

        if (accountsWithData.length === 0) return;

        // Calculate category-level data using the same logic as renderDepartment
        const categoryData = getCategoryMonthlyExpense(category, dept.name, projectedGross, deptWeights);

        if (categoryData.annual === 0 && categoryData.accountMonthly.size === 0) return;

        // Build account-level export data
        const exportAccounts: ExportAccount[] = [];
        accountsWithData.forEach(acc => {
          const accMonthly = categoryData.accountMonthly.get(acc.code);
          if (!accMonthly) return;

          // Calculate annual total from monthly
          let accAnnual = 0;
          for (let m = 1; m <= 12; m++) {
            accAnnual += accMonthly[`month${m}` as keyof MonthlyTotals] || 0;
          }

          if (accAnnual === 0) return;

          // Calculate percentage of gross (for display)
          const percentOfGross = projectedGross > 0 ? (accAnnual / projectedGross) * 100 : undefined;

          exportAccounts.push({
            code: acc.code,
            description: acc.description,
            monthlyValues: accMonthly,
            annualTotal: accAnnual,
            percentOfGross,
          });
        });

        if (exportAccounts.length > 0 || categoryData.annual > 0) {
          exportCategories.push({
            name: category.name,
            displayName: category.displayName,
            monthlyValues: categoryData.monthly,
            annualTotal: categoryData.annual,
            accounts: exportAccounts,
          });
        }
      });
    }

    // Add expenses row (except for OTHERS which is income-only)
    if (dept.name !== 'OTHERS') {
      rows.push({
        label: 'Total Expenses',
        monthlyValues: monthlyExpenses,
        annualTotal: projectedExpenses,
      });
    }

    // Add profit row (except for MISC and OTHERS)
    if (dept.name !== 'MISC' && dept.name !== 'OTHERS') {
      rows.push({
        label: 'Operating Profit',
        monthlyValues: monthlyProfit,
        annualTotal: projectedProfit,
        isTotalRow: true,
      });
    }

    return {
      name: dept.name,
      displayName: dept.name === 'MISC' ? displayName : `${displayName} Department`,
      rows,
      expenseCategories: exportCategories.length > 0 ? exportCategories : undefined,
    };
  });
}
